import { ExamCourseLanguage, ExamCourseType } from '@exam-center/graphql';
import { useEffect, useMemo } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { AVERAGE_SCORE_TEMPLATES } from '../../constants';
import {
  type DuringAndPostExamSettingsSectionFormOutputs,
  type DuringAndPostExamSettingsSectionFormInputs,
  type ExamCourseWithDuringAndPostExamSettings,
} from '../../containers/during-and-post-exam-settings-section-form/types';

import mapExamCourseSettingsToDuringAndPostExamSettingsFormData from './utils';

interface DuringAndPostExamSettingsFormLogicProps {
  form: UseFormReturn<
    DuringAndPostExamSettingsSectionFormInputs,
    unknown,
    DuringAndPostExamSettingsSectionFormOutputs
  >;
  examCourse: ExamCourseWithDuringAndPostExamSettings | undefined;
}

export default function useDuringAndPostExamSettingsFormLogic({
  form,
  examCourse,
}: DuringAndPostExamSettingsFormLogicProps) {
  const { reset, watch } = form;

  useEffect(() => {
    reset(mapExamCourseSettingsToDuringAndPostExamSettingsFormData(examCourse?.settings));
  }, [examCourse, reset]);

  const [
    questionnaireEnabled,
    shouldIssue,
    deliveryTimeType,
    expiryType,
    passTemplate,
    failTemplate,
    attachDocumentEnabled,
  ] = watch([
    'questionnaire.enabled',
    'certificate.shouldIssue',
    'certificate.deliveryTime.type',
    'certificate.expiry.type',
    'certificate.template.passTemplate',
    'certificate.template.failTemplate',
    'attachDocument.enabled',
  ]);

  const disabledFields = useMemo(() => {
    return {
      'certificate.template': shouldIssue === 'false',
      'certificate.deliveryTime.delay': deliveryTimeType === 'immediately' || !deliveryTimeType,
      'certificate.expiry.delay': expiryType !== 'delay',
    };
  }, [shouldIssue, deliveryTimeType, expiryType]);

  const disabledOptions = useMemo(() => {
    return {
      'certificate.deliveryTime.type.immediately':
        AVERAGE_SCORE_TEMPLATES.includes(passTemplate) ||
        AVERAGE_SCORE_TEMPLATES.includes(failTemplate),
      'certificate.expiry.type.delay': examCourse?.type === ExamCourseType.Mock,
      'certificate.shouldIssue.true':
        examCourse?.type === ExamCourseType.Mock && examCourse.language === ExamCourseLanguage.En,
      'resultSend.portal.true': examCourse?.type === ExamCourseType.Mock,
    };
  }, [passTemplate, failTemplate, examCourse?.type, examCourse?.language]);

  const visibleFields = useMemo(() => {
    return {
      condition: shouldIssue === 'true',
      averageScoreCondition:
        AVERAGE_SCORE_TEMPLATES.includes(passTemplate) ||
        AVERAGE_SCORE_TEMPLATES.includes(failTemplate),
      attachDocument: attachDocumentEnabled === 'true',
      questionnaireTemplate: questionnaireEnabled === 'true',
    };
  }, [shouldIssue, passTemplate, failTemplate, attachDocumentEnabled, questionnaireEnabled]);

  return {
    disabledFields,
    disabledOptions,
    visibleFields,
    passTemplate,
    failTemplate,
    attachDocumentEnabled,
  };
}
